import React, { useState } from 'react';
import { Form } from 'react-final-form';
import { Flex, Text, Box, Card, Button } from '../design-system';
import styled from 'styled-components';
import FieldColumn from './FieldColumn';
import EditButton from './EditButton';
import DeleteButton from './DeleteButton';
import SaveButton from './SaveButton';
import { formatTime, renderMultilineText } from '../helpers/utils';

const StyleForm = styled.form`
  width: 100%;
`;

interface ActionNoteFormProps {
  handleDeleteNote: (id: any) => void;
  handleUpdateNote: (values: any) => void;
  note: string;
  id: any;
}

const ActionNoteForm = ({
  handleDeleteNote,
  handleUpdateNote,
  note,
  id,
}: ActionNoteFormProps) => {
  return (
    <Form
      onSubmit={handleUpdateNote}
      initialValues={{ note, id }}
      render={({ handleSubmit, submitting }) => (
        <StyleForm onSubmit={handleSubmit}>
          <Flex>
            <FieldColumn
              label="Edit Note"
              name="note"
              multiline
              rows={note.length > 100 ? 5 : 3}
            />
          </Flex>
          <Button.Group justifyContent="flex-end">
            <Box>
              <SaveButton
                type="submit"
                fontSize={25}
                size="xs"
                disabled={submitting}
              />
            </Box>
            <Box>
              <DeleteButton
                onClick={() => handleDeleteNote(id)}
                fontSize={25}
                size="xs"
              />
            </Box>
          </Button.Group>
        </StyleForm>
      )}
    />
  );
};

interface ActionNoteProps {
  canEdit: boolean;
  note: string;
  creator: { name: string };
  created_at: string;
  id: any;
  handleDeleteNote: (id: any) => void;
  handleUpdateNote: (values: any) => void;
}

const ActionNote = ({ ...props }: ActionNoteProps) => {
  const { canEdit, note, creator, created_at } = props;
  const [isEditing, setIsEditing] = useState(false);
  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };

  return (
    <Flex justifyContent="space-between">
      <Box width="100%">
        {isEditing ? (
          <Flex>
            <ActionNoteForm {...props} />
          </Flex>
        ) : (
          <Flex>
            <Text fontSize={13}>{renderMultilineText(note)}</Text>
          </Flex>
        )}
        <Flex mt={2} alignItems="center">
          <Text fontWeight="bold" fontSize={12}>
            Created by: {creator.name} {formatTime(created_at)}
          </Text>
        </Flex>
      </Box>
      {canEdit && (
        <Box>
          <EditButton toggleEdit={toggleEditMode} />
        </Box>
      )}
    </Flex>
  );
};

interface ActionNotesProps {
  row: {
    original: {
      project_notes?: any[];
      client_notes?: any[];
      canEdit: boolean;
      handleDeleteNote: (id: any) => void;
      handleUpdateNote: (values: any) => void;
    };
  };
}

const ActionNotes = ({
  row: {
    original: {
      project_notes,
      client_notes,
      canEdit,
      handleDeleteNote,
      handleUpdateNote,
    },
  },
}: ActionNotesProps) => {
  const notes = project_notes || client_notes || [];

  if (notes.length > 0) {
    return (
      <Flex flexDirection="column" justifyContent="center">
        <Text fontWeight="bold" fontSize={14} mb={1}>
          Notes
        </Text>
        {notes.map((note) => (
          <Card p={2} mb={1} key={note.id}>
            <ActionNote
              canEdit={canEdit}
              {...note}
              handleDeleteNote={handleDeleteNote}
              handleUpdateNote={handleUpdateNote}
            />
          </Card>
        ))}
      </Flex>
    );
  }
  return (
    <Flex justifyContent="center">
      <Text fontWeight="bold">No Notes</Text>
    </Flex>
  );
};

export default ActionNotes;
